import { ISelectServiceGroupsHook } from '@models';
import { PagedServiceGroupRequest, ServiceGroup, ServiceGroupService } from '@services';
import axios from 'axios';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PAGE_SIZE = 20;

interface IProps {
  enabled?: boolean;
}

export const useSelectServiceGroups = ({ enabled = true }: IProps = {}): ISelectServiceGroupsHook => {
  const [serviceGroups, setServiceGroups] = useState<ServiceGroup[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skipCount, setSkipCount] = useState(0);
  const [keyword, setKeyword] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const cancelRef = useRef<{ cancel: () => void } | null>(null);
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (input: { skip: number; search: string | undefined; append: boolean }) => {
      cancelRef.current?.cancel();
      const source = axios.CancelToken.source();
      cancelRef.current = source;
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      setLoading(true);

      const request: PagedServiceGroupRequest = {
        keyword: input.search,
        maxResultCount: PAGE_SIZE,
        skipCount: input.skip,
      };

      try {
        const result = await ServiceGroupService.getAll(request, source.token);

        if (requestId !== requestIdRef.current) return;

        setServiceGroups((prev) => {
          if (!input.append) return result.items;
          const seen = new Set(prev.map((s) => s.id));
          const merged = [...prev];
          result.items.forEach((s) => {
            if (!seen.has(s.id)) merged.push(s);
          });
          return merged;
        });
        setTotalCount(result.totalCount);
        setLoading(false);
      } catch (error) {
        if (axios.isCancel(error)) return;
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!enabled) return;
    fetchPage({ skip: skipCount, search: keyword, append: skipCount > 0 });
  }, [enabled, skipCount, keyword, fetchPage]);

  useEffect(() => {
    return () => {
      cancelRef.current?.cancel();
    };
  }, []);

  const debouncedSetKeyword = useMemo(
    () =>
      debounce((text: string | undefined) => {
        setSkipCount(0);
        setKeyword(text && text.length > 0 ? text : undefined);
      }, 300),
    []
  );

  const handleSearch = useCallback(
    (text: string | undefined) => {
      debouncedSetKeyword(text);
    },
    [debouncedSetKeyword]
  );

  const loadMore = useCallback(() => {
    if (loading) return;
    if (serviceGroups.length >= totalCount) return;
    setSkipCount((prev) => prev + PAGE_SIZE);
  }, [loading, serviceGroups.length, totalCount]);

  const reset = useCallback(() => {
    debouncedSetKeyword.cancel();
    cancelRef.current?.cancel();
    setServiceGroups([]);
    setTotalCount(0);
    setSkipCount(0);
    setKeyword(undefined);
    setLoading(false);
  }, [debouncedSetKeyword]);

  return {
    serviceGroups,
    totalCount,
    pageSize: PAGE_SIZE,
    skipCount,
    loading,
    hasMore: serviceGroups.length < totalCount,
    handleSearch,
    loadMore,
    reset,
  };
};
